/**
 * 海外仓库管理前端接口
 * @author ChaseZhang
 * @since 2025-01-27
 */

import { httpClient as request } from '@/utils/http'

// ==================== 类型定义 ====================

/**
 * 仓库类型枚举
 */
export enum WarehouseType {
  /** 亚马逊仓 */
  AMAZON = 0,
  /** 易仓 */
  ECANG = 1,
  /** 其他 */
  OTHER = 2,
  /** 工厂仓 */
  FACTORY = 3,
  /** FBA 仓 */
  FBA = 4,
  /** 3PL 仓 */
  THIRD_PARTY = 5,
  /** 自有仓 */
  SELF_OWNED = 6,
  /** 海外仓 */
  OVERSEAS = 7,
  /** 中转仓 */
  TRANSIT = 8
}

/**
 * 启用状态枚举
 */
export enum EnabledStatus {
  /** 禁用 */
  DISABLED = 0,
  /** 启用 */
  ENABLED = 1
}

// 基础分页请求
export interface PageRequest {
  pageNum: number
  pageSize: number
}

// 分页结果
export interface PageResultVO<T> {
  records: T[]
  total: number
  pageNum: number
  pageSize: number
}

/** 仓库语言版本明细 */
export interface OverseasWarehouseLanguageVO {
  /** 语言版本ID；历史兼容数据首次保存前可能为空 */
  id?: string;
  /** 语种：zh-CN-中文，en-US-英语 */
  locale: 'zh-CN' | 'en-US';
  /** 州/省 */
  stateProvince?: string;
  /** 城市 */
  city?: string;
  /** 邮政编码 */
  postalCode?: string;
  /** 详细地址 */
  addressDetail: string;
  /** 仓库联系人 */
  contactName?: string;
  /** 仓库联系电话 */
  contactPhone?: string;
}

/** 仓库语言版本保存模型 */
export type OverseasWarehouseLanguageSaveRequest = OverseasWarehouseLanguageVO

// 批量删除请求
export interface BatchDeleteRequest {
  ids: string[]
}

/**
 * 海外仓库管理响应模型
 */
export interface OverseasWarehouseVO {
  /** 唯一标识 */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 仓库编码，如 US-AMZ-001 */
  warehouseCode: string;
  /** 仓库类型：0-亚马逊仓, 1-易仓, 2-其他, 3-工厂仓, 4-FBA仓, 5-3PL仓, 6-自有仓, 7-海外仓, 8-中转仓 */
  warehouseType: WarehouseType;
  /** 数据来源 */
  dataSource?: number;
  /** 仓库中文名称，如 亚马逊洛杉矶仓库 */
  warehouseNameCn: string;
  /** 仓库英文名称，如 Amazon Los Angeles Warehouse */
  warehouseNameEn?: string;
  /** 仓库所在国家，ISO国家代码 */
  countryCode: string;
  /** 州/省份，如 California */
  stateProvince?: string;
  /** 城市，如 Los Angeles */
  city: string;
  /** 邮政编码，如 90001 */
  postalCode?: string;
  /** 详细地址，如 1234 Main Street, Los Angeles, CA */
  addressDetail: string;
  /** 仓库联系人 */
  contactPerson?: string;
  /** 仓库联系电话 */
  contactPhone?: string;
  /** 启用状态：1-启用, 0-禁用 */
  enabled: EnabledStatus;
  /** 创建时间 */
  dateCreated?: string;
  /** 更新时间 */
  dateModified?: string;
  /** 创建人 */
  createdBy?: string;
  /** 修改人 */
  modifiedBy?: string;
  /** 是否为平台数据 */
  isPlatformData?: boolean;
  /** 当前租户是否已存在同仓库代码的仓库 */
  tenantCopied?: boolean;
  /** 仓库语言版本明细 */
  languageVersions?: OverseasWarehouseLanguageVO[];
}

/**
 * 海外仓库查询请求模型
 */
export interface OverseasWarehouseQueryRequest extends PageRequest {
  /** 租户ID */
  tenantId?: string;
  /** 关键词搜索 */
  keyword?: string;
  /** 仓库编码，支持模糊查询 */
  warehouseCode?: string;
  /** 仓库名称，按语种筛选；未指定语种时同时匹配中英文名称 */
  warehouseName?: string;
  /** 语言版本语种：zh-CN-中文，en-US-英文；同时决定仓库名称匹配字段 */
  languageCode?: string;
  /** 仓库类型：0-亚马逊仓, 1-易仓, 2-其他, 3-工厂仓, 4-FBA仓, 5-3PL仓, 6-自有仓, 7-海外仓, 8-中转仓 */
  warehouseType?: WarehouseType;
  /** 数据来源 */
  dataSource?: number;
  /** 仓库中文名称，支持模糊查询 */
  warehouseNameCn?: string;
  /** 仓库英文名称，支持模糊查询 */
  warehouseNameEn?: string;
  /** 仓库所在国家，ISO国家代码 */
  countryCode?: string;
  /** 州/省份，支持模糊查询 */
  stateProvince?: string;
  /** 城市，支持模糊查询 */
  city?: string;
  /** 邮政编码 */
  postalCode?: string;
  /** 详细地址，支持模糊查询 */
  addressDetail?: string;
  /** 启用状态：1-启用, 0-禁用 */
  enabled?: EnabledStatus;
  /** 按指定平台数据ID过滤 */
  ids?: string[];
  /** 按筛选条件复制时排除的平台数据ID */
  excludePlatformIds?: string[];
  /** 当前租户是否已存在同仓库代码的仓库 */
  tenantCopied?: boolean;
}

/**
 * 海外仓库模糊搜索请求
 */
export interface FuzzySearchRequest {
  /** 租户ID */
  tenantId?: string;
  /** 搜索关键字 */
  keyword: string;
  /** 启用状态 */
  enabled?: EnabledStatus;
  /** 仓库类型 */
  warehouseType?: WarehouseType;
  /** 国家代码 */
  countryCode?: string;
}

/**
 * 海外仓库保存请求模型
 */
export interface OverseasWarehouseSaveRequest {
  /** 主键ID，更新时传递 */
  id?: string;
  /** 租户ID */
  tenantId?: string;
  /** 仓库编码，如 US-AMZ-001 */
  warehouseCode: string;
  /** 仓库类型：0-亚马逊仓, 1-易仓, 2-其他, 3-工厂仓, 4-FBA仓, 5-3PL仓, 6-自有仓, 7-海外仓, 8-中转仓 */
  warehouseType: WarehouseType;
  /** 数据来源 */
  dataSource?: number;
  /** 仓库中文名称，如 亚马逊洛杉矶仓库 */
  warehouseNameCn: string;
  /** 仓库英文名称，如 Amazon Los Angeles Warehouse */
  warehouseNameEn?: string;
  /** 仓库所在国家，ISO国家代码，支持选项：US(美国)、CA(加拿大)、GB(英国)、DE(德国)、FR(法国)、JP(日本)、AU(澳大利亚) */
  countryCode: string;
  /** 州/省份，如 California */
  stateProvince?: string;
  /** 城市，如 Los Angeles */
  city: string;
  /** 邮政编码，如 90001 */
  postalCode?: string;
  /** 详细地址，如 1234 Main Street, Los Angeles, CA */
  addressDetail: string;
  /** 仓库联系人 */
  contactPerson?: string;
  /** 仓库联系电话 */
  contactPhone?: string;
  /** 启用状态：1-启用, 0-禁用 */
  enabled?: EnabledStatus;
  /** 仓库语言版本明细；至少保留一种语种 */
  languageVersions?: OverseasWarehouseLanguageSaveRequest[];
}

export interface PlatformCopyResult {
  matchedCount: number;
  requestedCount: number;
  excludedCount: number;
  copiedCount: number;
  skippedCount: number;
  failedCount?: number;
  errorMessage?: string;
}

export interface CopyPlatformWarehouseRequest {
  selectionMode: 'IDS' | 'QUERY';
  platformIds?: string[];
  query?: OverseasWarehouseQueryRequest;
  excludePlatformIds?: string[];
}

export interface CopyPlatformWarehouseResponse extends Partial<PlatformCopyResult> {
  copyMode: 'SYNC' | 'TASK';
  taskRecordId?: string;
  status?: number;
  existingActiveTask?: boolean;
}

// API基础路径
const API_BASE = '/mdm/overseasWarehouse'

// ==================== API 接口 ====================

/**
 * 海外仓库管理API
 */
export const overseasWarehouseApi = {
  // ==================== 核心CRUD接口 ====================

  /**
   * 分页查询海外仓库列表
   */
  queryPage: (params: OverseasWarehouseQueryRequest) => {
    return request.post<PageResultVO<OverseasWarehouseVO>>(`${API_BASE}/queryPage`, params)
  },

  /**
   * 查询海外仓库列表
   */
  queryList: (params: OverseasWarehouseQueryRequest) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryList`, params)
  },

  /**
   * 模糊搜索海外仓库
   */
  search: (params: FuzzySearchRequest) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/search`, params)
  },

  /**
   * 根据ID获取海外仓库详情
   */
  getById: (id: string) => {
    return request.post<OverseasWarehouseVO>(`${API_BASE}/getById`, { id })
  },

  /**
   * 保存海外仓库（新增或更新）
   */
  save: (params: OverseasWarehouseSaveRequest) => {
    return request.post<boolean>(`${API_BASE}/save`, params)
  },

  /**
   * 删除海外仓库
   */
  delete: (id: string) => {
    return request.post<boolean>(`${API_BASE}/delete`, { id })
  },

  // ==================== 批量操作接口 ====================

  /**
   * 批量保存海外仓库
   */
  batchSave: (requests: OverseasWarehouseSaveRequest[]) => {
    return request.post<boolean>(`${API_BASE}/batchSave`, { requests })
  },

  /**
   * 批量删除海外仓库
   */
  batchDelete: (params: BatchDeleteRequest) => {
    return request.post<boolean>(`${API_BASE}/batchDelete`, params)
  },

  /**
   * 更新启用状态
   */
  updateStatus: (ids: string[], enabled: EnabledStatus) => {
    return request.post<boolean>(`${API_BASE}/updateStatus`, { ids, enabled })
  },

  /**
   * 批量更新启用状态
   */
  batchUpdateStatus: (ids: string[], enabled: EnabledStatus) => {
    return request.post<boolean>(`${API_BASE}/batchUpdateStatus`, { ids, enabled })
  },

  /**
   * 查询可复制的平台海外仓库（分页）
   */
  queryPlatformPage: (params: OverseasWarehouseQueryRequest) => {
    return request.post<PageResultVO<OverseasWarehouseVO>>(`${API_BASE}/queryPlatformPage`, params)
  },

  /**
   * 查询可复制的平台海外仓库（列表）
   */
  queryPlatformList: (params: OverseasWarehouseQueryRequest) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryPlatformList`, params)
  },

  /**
   * 复制指定平台海外仓库
   */
  batchAssociate: (platformIds: string[]) => {
    return request.post<boolean>(`${API_BASE}/batchAssociate`, { platformIds })
  },

  /**
   * 按当前筛选条件复制平台海外仓库
   */
  batchAssociateByQuery: (params: OverseasWarehouseQueryRequest) => {
    return request.post<PlatformCopyResult>(`${API_BASE}/batchAssociateByQuery`, params)
  },

  /**
   * 统一提交平台海外仓库复制，后端按实际候选数决定同步或后台任务
   */
  copyPlatform: (params: CopyPlatformWarehouseRequest) => {
    return request.post<CopyPlatformWarehouseResponse>(`${API_BASE}/copyPlatform`, params)
  },

  // ==================== 专项查询接口 ====================

  /**
   * 根据仓库类型查询海外仓库列表
   */
  queryByWarehouseType: (warehouseType: WarehouseType, tenantId?: string) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryByWarehouseType`, { warehouseType, tenantId })
  },

  /**
   * 根据国家代码查询海外仓库列表
   */
  queryByCountryCode: (countryCode: string, tenantId?: string) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryByCountryCode`, { countryCode, tenantId })
  },

  /**
   * 根据仓库编码查询海外仓库
   */
  queryByWarehouseCode: (warehouseCode: string, tenantId?: string) => {
    return request.post<OverseasWarehouseVO>(`${API_BASE}/queryByWarehouseCode`, { warehouseCode, tenantId })
  },

  /**
   * 查询启用状态的海外仓库列表
   */
  queryEnabled: (tenantId?: string) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryEnabled`, { tenantId })
  },

  /**
   * 查询亚马逊仓库列表
   */
  queryAmazonWarehouses: (tenantId?: string) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryAmazonWarehouses`, { tenantId })
  },

  /**
   * 查询易仓仓库列表
   */
  queryEcangWarehouses: (tenantId?: string) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryEcangWarehouses`, { tenantId })
  },

  /**
   * 查询其他类型仓库列表
   */
  queryOtherWarehouses: (tenantId?: string) => {
    return request.post<OverseasWarehouseVO[]>(`${API_BASE}/queryOtherWarehouses`, { tenantId })
  }
}

// ==================== 便捷服务 ====================

/**
 * 海外仓库管理便捷服务
 */
export const OverseasWarehouseService = {
  // 核心CRUD
  queryPage: overseasWarehouseApi.queryPage,
  queryList: overseasWarehouseApi.queryList,
  search: overseasWarehouseApi.search,
  getById: overseasWarehouseApi.getById,
  save: overseasWarehouseApi.save,
  delete: overseasWarehouseApi.delete,

  // 批量操作
  batchSave: overseasWarehouseApi.batchSave,
  batchDelete: overseasWarehouseApi.batchDelete,
  updateStatus: overseasWarehouseApi.updateStatus,
  batchUpdateStatus: overseasWarehouseApi.batchUpdateStatus,
  queryPlatformPage: overseasWarehouseApi.queryPlatformPage,
  queryPlatformList: overseasWarehouseApi.queryPlatformList,
  batchAssociate: overseasWarehouseApi.batchAssociate,
  batchAssociateByQuery: overseasWarehouseApi.batchAssociateByQuery,
  copyPlatform: overseasWarehouseApi.copyPlatform,

  // 专项查询
  queryByWarehouseType: overseasWarehouseApi.queryByWarehouseType,
  queryByCountryCode: overseasWarehouseApi.queryByCountryCode,
  queryByWarehouseCode: overseasWarehouseApi.queryByWarehouseCode,
  queryEnabled: overseasWarehouseApi.queryEnabled,
  queryAmazonWarehouses: overseasWarehouseApi.queryAmazonWarehouses,
  queryEcangWarehouses: overseasWarehouseApi.queryEcangWarehouses,
  queryOtherWarehouses: overseasWarehouseApi.queryOtherWarehouses
}

export default overseasWarehouseApi
